import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { Calendar, Check } from 'lucide-react';
import { format } from 'date-fns';
import { AnchoredPanel } from 'components/UI/AnchoredPanel';
import { border, font } from 'settings/tokens';
import { fieldSurfaceSx, FIELD_HEIGHT } from 'components/UI/fieldMetrics';
import { FieldLabel } from 'components/UI/FieldLabel';
import { menuRowToneSx, MENU_ROW_LABEL_SX } from 'components/UI/menuRowTone';

export type DateRangeKey = 'last_month' | 'last_3m' | 'last_6m' | 'last_12m' | null;

export type CertificatesDateRangeFilterProps = {
  mobile?: boolean;
  selectedRange: DateRangeKey;
  selectedDateFrom: Date | null;
  selectedDateTo: Date | null;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onClear: () => void;
  onSelectRange: (key: NonNullable<DateRangeKey>) => void;
  onSelectDate: (date: Date | null) => void;
  fieldLabel?: string;
};

export const CertificatesDateRangeFilter = ({
  mobile,
  selectedRange,
  selectedDateFrom,
  selectedDateTo,
  open,
  onOpen,
  onClose,
  onClear,
  onSelectRange,
  onSelectDate,
  // "Last activity", not "Activity": this control is a DATE picker, and a bare
  // "Activity" over a date field reads as a category axis rather than a date
  // one. Every sibling sheet names its date field the same way ("Scan date",
  // "Ownership date").
  fieldLabel = 'Last activity',
}: CertificatesDateRangeFilterProps) => {
  const hasLabel = Boolean(fieldLabel);
  const label =
    selectedRange === 'last_month'
      ? 'Last month'
      : selectedRange === 'last_3m'
      ? 'Last 3 months'
      : selectedRange === 'last_6m'
      ? 'Last 6 months'
      : selectedRange === 'last_12m'
      ? 'Last 12 months'
      : selectedDateFrom && selectedDateTo
      ? format(selectedDateFrom, 'MMM d, yyyy') + ' - ' + format(selectedDateTo, 'MMM d, yyyy')
      : 'Select date';

  if (mobile) {
    return (
      <>
        {hasLabel && (
          <FieldLabel
            label={fieldLabel}
          />
        )}
        <Box
          onClick={onOpen}
          sx={theme => ({
            ...fieldSurfaceSx(theme),
            // Was a bare 44 while the desktop branch of THIS SAME component used 40 —
            // one control, two heights, in one file. Both are the shared token now.
            height: FIELD_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            color: 'text.secondary',
            gap: 1,
            cursor: 'pointer',
            position: 'relative',
          })}
        >
          <Calendar size={16} />
          <Typography color={'text.secondary'} sx={{ flex: 1 }}>
            {label}
          </Typography>
          {(selectedRange || selectedDateFrom || selectedDateTo) && (
            <Box
              onClick={e => {
                e.stopPropagation();
                onClear();
              }}
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              ×
            </Box>
          )}
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {hasLabel && (
        <FieldLabel
          label={fieldLabel}
        />
      )}
      <Box
        onClick={onOpen}
        sx={theme => ({
          ...fieldSurfaceSx(theme),
          height: FIELD_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          color: 'text.secondary',
          gap: 1,
          cursor: 'pointer',
          position: 'relative',
        })}
      >
        <Calendar size={16} />
        <Typography color={'text.secondary'} sx={{ flex: 1 }}>
          {label}
        </Typography>
        {(selectedRange || selectedDateFrom || selectedDateTo) && (
          <Box
            onClick={e => {
              e.stopPropagation();
              onClear();
            }}
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            ×
          </Box>
        )}
      </Box>

      <AnchoredPanel open={open} onClickAway={onClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 10,
              mt: 1,
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              border: '1px solid',
              borderColor: border.subtle,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              display: 'flex',
              minHeight: 400,
            }}
          >
            <Box sx={{ flex: 1, p: 2, borderRight: '1px solid', borderRightColor: border.subtle }}>
              <Stack spacing={0.5}>
                {[
                  { key: 'last_month', label: 'Last month' },
                  { key: 'last_3m', label: 'Last 3 months' },
                  { key: 'last_6m', label: 'Last 6 months' },
                  { key: 'last_12m', label: 'Last 12 months' },
                ].map((opt: any) => {
                  const selected = selectedRange === opt.key;
                  return (
                    <Stack
                      key={opt.key}
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      onClick={() => onSelectRange(opt.key as any)}
                      /* Same shared tone as the mobile sheet for these presets
                         (`components/UI/menuRowTone`), so the two surfaces of one
                         filter agree. The selected row keeps its own resting wash
                         and darkens on hover rather than falling back to the
                         unselected `grey[50]`. */
                      sx={theme => {
                        const tone = menuRowToneSx(theme, { selected });
                        return {
                          cursor: 'pointer',
                          px: 1.5,
                          py: 1.25,
                          borderRadius: '12px',
                          backgroundColor: 'transparent',
                          ...tone,
                          ...(selected && {
                            backgroundColor: theme.palette.grey[100],
                            '&:hover, &:focus-visible': {
                              ...tone['&:hover, &:focus-visible'],
                              backgroundColor: theme.palette.grey[200],
                            },
                          }),
                        };
                      }}
                    >
                      {/* No weight step for selection — colour carries it. */}
                      <Typography sx={{ fontSize: font.label, fontWeight: 400, ...MENU_ROW_LABEL_SX }}>
                        {opt.label}
                      </Typography>
                      {/* `currentColor`, so the tick follows the row into the
                          accent on hover instead of staying black. */}
                      {selected && <Check size={18} color='currentColor' strokeWidth={2} />}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
            <Box sx={{ flex: 1, p: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={selectedDateFrom ? new Date(selectedDateFrom) : null}
                  onChange={newDate => onSelectDate(newDate ? new Date(newDate) : null)}
                  views={['year', 'month', 'day']}
                  sx={{
                    '& .MuiYearCalendar-root': { width: '100%' },
                    '& .MuiMonthCalendar-root': { width: '100%' },
                    '& .MuiPickersArrowSwitcher-button': {
                      backgroundColor: 'transparent',
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'transparent' },
                    },
                    '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': {
                      backgroundColor: 'transparent',
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                    },
                    '& .MuiPickersCalendarHeader-switchViewButton': {
                      backgroundColor: 'transparent',
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                    },
                    '& .MuiPickersDay-today': {
                      border: '1px solid hsla(271, 81%, 56%, 1)',
                      borderRadius: '50%',
                    },
                    '& .MuiPickersDay-root.Mui-selected': {
                      backgroundColor: 'hsla(0, 0%, 0%, 1)',
                      color: 'hsla(0, 0%, 100%, 1)',
                      '&:hover': { backgroundColor: 'hsla(0, 0%, 0%, 1)' },
                      '&:focus': { backgroundColor: 'hsla(0, 0%, 0%, 1)' },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
      </AnchoredPanel>
    </Box>
  );
};

export default CertificatesDateRangeFilter;
